import { useState } from 'react';
import { Controller, type SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { type WebidStartRequest } from '../webidForm.types';
import { webidCountries } from '@/data/webidCountries';
import getGroupedCountriesOptions from '@/utils/getGroupedCountriesOptions';
import useApi from '@/hooks/useApi';
import usePhoneFieldValidation from '@/hooks/usePhoneFieldValidation';
import IcIdCard from '@/assets/icons/IdCard.svg?react';
import IcMonitorSmartphone from '@/assets/icons/MonitorSmartphone.svg?react';
import IcWifi from '@/assets/icons/Wifi.svg?react';
import IcCheck from '@/assets/icons/Check.svg?react';
import Button from '@/components/Button';
import FormInput from '@/components/FormInput';
import FormPhoneInput from '@/components/FormPhoneInput';
import FormSelect from '@/components/FormSelect';
import NeedTile from './NeedTile';
import styles from './StartNewVerification.module.scss';

interface StartNewVerificationProps {
  loading: boolean;
  /** Pre-fills the mobile number field (E.164, e.g. user's verified phone). */
  initialPhone?: string;
  /** Pre-fills the address country (ISO 3166-1 alpha-2, any case). */
  initialCountry?: string;
  onBack: () => void;
  onSubmit: (data: WebidStartRequest) => void;
}

type Step = 'intro' | 'form';

const COUNTRY_CODES = webidCountries;

// Normalise a pre-fill to a supported uppercase ISO code, defaulting to AT.
const normaliseCountry = (raw: string | undefined): string => {
  const upper = raw?.toUpperCase() ?? '';
  return COUNTRY_CODES.includes(upper) ? upper : 'AT';
};

// `toISOString()` is UTC-shifted and would flip the day for evening timezones
// behind UTC. Format the local Y-M-D directly.
const todayIsoLocal = (): string => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const StartNewVerification = ({
  loading,
  initialPhone,
  initialCountry,
  onBack,
  onSubmit,
}: StartNewVerificationProps) => {
  const { t } = useTranslation(['onboarding', 'vatCountries']);

  const [step, setStep] = useState<Step>('intro');

  const [checkPhoneNumber] = useApi('phoneNumber.check');

  const {
    control,
    register,
    handleSubmit,
    setValue,
    setError,
    clearErrors,
    formState: { errors, isValid },
  } = useForm<WebidStartRequest>({
    mode: 'onChange',
    defaultValues: {
      dateOfBirth: '',
      sex: undefined,
      cell: initialPhone ?? '',
      address: {
        street: '',
        streetNo: '',
        zip: '',
        city: '',
        country: normaliseCountry(initialCountry),
      },
    },
  });

  const { handlePhoneChange, handlePhoneBlur, validatePhoneBeforeSubmit } =
    usePhoneFieldValidation<WebidStartRequest>({
      fieldName: 'cell',
      checkPhoneNumber,
      clearErrors,
      setError,
      setValue,
      errorMessage: t('onboarding:kycSelection.webidForm.cellInvalid'),
    });

  const countryOptions = getGroupedCountriesOptions(t, COUNTRY_CODES, false);

  const submit: SubmitHandler<WebidStartRequest> = (data) => {
    onSubmit({
      ...data,
      cell: data.cell.replace(/\s+/g, ''),
      address: {
        street: data.address.street.trim(),
        streetNo: data.address.streetNo.trim(),
        zip: data.address.zip.trim(),
        city: data.address.city.trim(),
        country: data.address.country,
      },
    });
  };

  if (step === 'form') {
    return (
      <section className={styles.card}>
        <h2 className={styles.title}>
          {t('onboarding:kycSelection.webidForm.title')}
        </h2>
        <div className={styles.desc}>
          {t('onboarding:kycSelection.webidForm.subtitle')}
        </div>

        <form
          className={styles.form}
          onSubmit={(e) => void handleSubmit(submit)(e)}
          noValidate
        >
          <div className={styles.formSection}>
            <h3 className={styles.eyebrow}>
              {t('onboarding:kycSelection.webidForm.personal')}
            </h3>
            <div className={styles.formRow}>
              <div className={styles.field}>
                <FormInput
                  id="webid-dob"
                  type="date"
                  label={t('onboarding:kycSelection.webidForm.dateOfBirth')}
                  max={todayIsoLocal()}
                  required
                  {...register('dateOfBirth', { required: true })}
                />
              </div>
              <div className={styles.field}>
                <FormSelect
                  id="webid-sex"
                  label={t('onboarding:kycSelection.webidForm.sex')}
                  defaultValue=""
                  required
                  {...register('sex', { required: true })}
                >
                  <option value="" disabled>
                    {t('onboarding:kycSelection.webidForm.sexPlaceholder')}
                  </option>
                  <option value="female">
                    {t('onboarding:kycSelection.webidForm.sexFemale')}
                  </option>
                  <option value="male">
                    {t('onboarding:kycSelection.webidForm.sexMale')}
                  </option>
                  <option value="diverse">
                    {t('onboarding:kycSelection.webidForm.sexDiverse')}
                  </option>
                  <option value="unknown">
                    {t('onboarding:kycSelection.webidForm.sexUnknown')}
                  </option>
                </FormSelect>
              </div>
            </div>
          </div>

          <div className={styles.formSection}>
            <h3 className={styles.eyebrow}>
              {t('onboarding:kycSelection.webidForm.contact')}
            </h3>
            <Controller
              control={control}
              name="cell"
              rules={{
                validate: (value) => validatePhoneBeforeSubmit(value),
              }}
              render={({ field }) => (
                <FormPhoneInput
                  id="webid-cell"
                  ref={field.ref}
                  label={t('onboarding:kycSelection.webidForm.cell')}
                  value={field.value}
                  errorMessage={errors.cell?.message}
                  autoComplete="tel"
                  required
                  onChange={(value) => {
                    field.onChange(value);
                    handlePhoneChange(value);
                  }}
                  onBlur={(event) => {
                    field.onBlur();
                    void handlePhoneBlur(event.target.value);
                  }}
                />
              )}
            />
            <div className={styles.hint}>
              {t('onboarding:kycSelection.webidForm.cellHint')}
            </div>
          </div>

          <div className={styles.formSection}>
            <h3 className={styles.eyebrow}>
              {t('onboarding:kycSelection.webidForm.address')}
            </h3>
            <div className={styles.formRowStreet}>
              <div className={styles.fieldGrow}>
                <FormInput
                  id="webid-street"
                  label={t('onboarding:kycSelection.webidForm.street')}
                  autoComplete="address-line1"
                  required
                  {...register('address.street', { required: true })}
                />
              </div>
              <div className={styles.fieldNarrow}>
                <FormInput
                  id="webid-streetNo"
                  label={t('onboarding:kycSelection.webidForm.streetNo')}
                  required
                  {...register('address.streetNo', { required: true })}
                />
              </div>
            </div>
            <div className={styles.formRow}>
              <div className={styles.field}>
                <FormInput
                  id="webid-zip"
                  label={t('onboarding:kycSelection.webidForm.zip')}
                  autoComplete="postal-code"
                  required
                  {...register('address.zip', { required: true })}
                />
              </div>
              <div className={styles.fieldGrow}>
                <FormInput
                  id="webid-city"
                  label={t('onboarding:kycSelection.webidForm.city')}
                  autoComplete="address-level2"
                  required
                  {...register('address.city', { required: true })}
                />
              </div>
            </div>
            <FormSelect
              id="webid-country"
              label={t('onboarding:kycSelection.webidForm.country')}
              required
              {...register('address.country', { required: true })}
            >
              {countryOptions}
            </FormSelect>
          </div>

          <div className={styles.actions}>
            <Button
              variant="text"
              onClick={() => setStep('intro')}
              disabled={loading}
            >
              {t('onboarding:kycSelection.newVerification.back')}
            </Button>
            <Button submit loading={loading} disabled={!isValid || loading}>
              {t('onboarding:kycSelection.webidForm.submit')}
            </Button>
          </div>
        </form>
      </section>
    );
  }

  const checklist = [
    {
      title: t('onboarding:kycRedesign.prepare.checklist.cameraTitle'),
      desc: t('onboarding:kycRedesign.prepare.checklist.cameraDesc'),
    },
    {
      title: t('onboarding:kycRedesign.prepare.checklist.lightingTitle'),
      desc: t('onboarding:kycRedesign.prepare.checklist.lightingDesc'),
    },
    {
      title: t('onboarding:kycRedesign.prepare.checklist.quietTitle'),
      desc: t('onboarding:kycRedesign.prepare.checklist.quietDesc'),
    },
    {
      title: t('onboarding:kycRedesign.prepare.checklist.credentialsTitle'),
      desc: t('onboarding:kycRedesign.prepare.checklist.credentialsDesc'),
    },
  ];

  const nextSteps = [
    {
      title: t('onboarding:kycRedesign.prepare.nextSteps.step1Title'),
      desc: t('onboarding:kycRedesign.prepare.nextSteps.step1Desc'),
    },
    {
      title: t('onboarding:kycRedesign.prepare.nextSteps.step2Title'),
      desc: t('onboarding:kycRedesign.prepare.nextSteps.step2Desc'),
    },
    {
      title: t('onboarding:kycRedesign.prepare.nextSteps.step3Title'),
      desc: t('onboarding:kycRedesign.prepare.nextSteps.step3Desc'),
    },
    {
      title: t('onboarding:kycRedesign.prepare.nextSteps.step4Title'),
      desc: t('onboarding:kycRedesign.prepare.nextSteps.step4Desc'),
    },
  ];

  return (
    <section className={styles.card}>
      <span className={styles.poweredBy}>
        {t('onboarding:kycRedesign.prepare.poweredBy')} <strong>WebID</strong>
        <span className={styles.poweredBySep} aria-hidden="true">
          &amp;
        </span>
        <strong>A-Trust</strong>
      </span>

      <h2 className={styles.title}>
        {t('onboarding:kycRedesign.prepare.title')}
      </h2>
      <div className={styles.desc}>
        {t('onboarding:kycRedesign.prepare.desc')}
      </div>

      <h3 className={styles.eyebrow}>
        {t('onboarding:kycRedesign.prepare.whatYouNeed')}
      </h3>

      <div className={styles.needsGrid}>
        <NeedTile
          icon={IcMonitorSmartphone}
          title={t('onboarding:kycSelection.newVerification.need1Title')}
          description={t('onboarding:kycSelection.newVerification.need1Desc')}
        />
        <NeedTile
          icon={IcIdCard}
          title={t('onboarding:kycSelection.newVerification.need2Title')}
          description={t('onboarding:kycSelection.newVerification.need2Desc')}
        />
        <NeedTile
          icon={IcWifi}
          title={t('onboarding:kycSelection.newVerification.need3Title')}
          description={t('onboarding:kycSelection.newVerification.need3Desc')}
        />
      </div>

      <div className={styles.checklist}>
        {checklist.map((item) => (
          <div key={item.title} className={styles.checkItem}>
            <span className={styles.checkIcon} aria-hidden="true">
              <IcCheck />
            </span>
            <span className={styles.checkText}>
              <strong>{item.title}</strong> {item.desc}
            </span>
          </div>
        ))}
      </div>

      <h3 className={styles.eyebrow}>
        {t('onboarding:kycRedesign.prepare.nextStepsTitle')}
      </h3>

      <ol className={styles.nextSteps}>
        {nextSteps.map((item, index) => (
          <li key={item.title} className={styles.nextStep}>
            <span className={styles.nextStepMarker} aria-hidden="true">
              <span
                className={styles.nextStepNum}
                data-last={index === nextSteps.length - 1}
              >
                {index + 1}
              </span>
            </span>
            <span className={styles.nextStepText}>
              <span className={styles.nextStepTitle}>{item.title}</span>
              <span className={styles.nextStepDesc}>{item.desc}</span>
            </span>
          </li>
        ))}
      </ol>

      <div className={styles.actions}>
        <Button variant="text" onClick={onBack} disabled={loading}>
          {t('onboarding:kycSelection.newVerification.back')}
        </Button>
        <Button onClick={() => setStep('form')} disabled={loading}>
          {t('onboarding:kycSelection.newVerification.start')}
        </Button>
      </div>
    </section>
  );
};

export default StartNewVerification;
